import { ui } from '@procuros/datachecks';

import { DisplayModes } from 'services/webedi/types';
import { DocumentType, DocumentTypeWithVirtual, ValueOf } from 'support/types';
import { SchemableFormVariablesContextProvider } from 'components/SchemableForm/contexts/SchemableFormVariablesContext/SchemableFormVariablesContextProvider';

import { DocumentBaseSection } from '../DocumentBaseSection/DocumentBaseSection';
import { DocumentHeaderInfo } from '../DocumentHeaderInfo/DocumentHeaderInfo';
import { DocumentLineItems } from '../DocumentLineItems/DocumentLineItems';
import { DocumentLocations } from '../DocumentLocations/DocumentLocations';
import { DocumentModificationGroups } from '../DocumentModificationGroups/DocumentModificationGroups';
import { DocumentParties } from '../DocumentParties/DocumentParties';
import { DocumentPaymentTerms } from '../DocumentPaymentTerms/DocumentPaymentTerms';
import { DocumentTransportUnits } from '../DocumentTransportUnits/DocumentTransportUnits';

type BaseProps = {
  uiConfig: Array<ui.Section> | undefined;
  documentType: ValueOf<DocumentTypeWithVirtual>;
  variables: Record<string, string>;
};

type EditableProps = BaseProps & {
  mode: DisplayModes.create | DisplayModes.edit;
  isSubmitting: boolean;
};

type ViewProps = BaseProps & {
  mode: DisplayModes.view;
  isSubmitting?: boolean;
};

type DocumentSectionsProps = (EditableProps | ViewProps) & {
  baseData?: Record<string, unknown>;
  revisedData?: Record<string, unknown>;
};

export const DocumentSections = ({
  mode,
  uiConfig,
  documentType,
  variables,
  isSubmitting,
  baseData,
  revisedData,
}: DocumentSectionsProps) => {
  const baseSections = baseData ?? {};
  const revisedSections = revisedData ?? {};

  return (
    <SchemableFormVariablesContextProvider variables={variables}>
      <div className="h-full divide-y divide-gray-200 *:not-first:pt-6 *:not-last:pb-8">
        {uiConfig?.map((config) => {
          switch (config.type) {
            case 'info':
              return (
                <DocumentHeaderInfo
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                />
              );
            case 'parties':
              return (
                <DocumentParties
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                  baseData={baseSections.parties as Array<unknown> | undefined}
                  revisedData={revisedSections.parties as Array<unknown> | undefined}
                />
              );
            case 'transport_units':
              return (
                <DocumentTransportUnits
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                  baseData={baseSections.transport_units as Array<unknown> | undefined}
                  revisedData={revisedSections.transport_units as Array<unknown> | undefined}
                />
              );
            case 'locations':
              return (
                <DocumentLocations
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                  baseData={baseSections.locations as Array<unknown> | undefined}
                  revisedData={revisedSections.locations as Array<unknown> | undefined}
                />
              );
            case 'line_items':
              return (
                <DocumentLineItems
                  key={config.type}
                  mode={mode}
                  config={config}
                  documentType={documentType as DocumentType}
                  isSubmitting={Boolean(isSubmitting)}
                  baseData={baseSections.line_items as Array<unknown> | undefined}
                  revisedData={revisedSections.line_items as Array<unknown> | undefined}
                />
              );
            case 'payment_terms':
              return (
                <DocumentPaymentTerms
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                  baseData={baseSections.payment_terms as Array<unknown> | undefined}
                  revisedData={revisedSections.payment_terms as Array<unknown> | undefined}
                />
              );
            case 'global_modifications':
              return (
                <DocumentModificationGroups
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                  baseData={baseSections.modification_groups as Array<unknown> | undefined}
                  revisedData={revisedSections.modification_groups as Array<unknown> | undefined}
                />
              );
            default:
              return (
                <DocumentBaseSection
                  key={config.type}
                  mode={mode}
                  config={config}
                  isSubmitting={Boolean(isSubmitting)}
                  baseData={baseSections[config.type] as Array<unknown> | undefined}
                  revisedData={revisedSections[config.type] as Array<unknown> | undefined}
                />
              );
          }
        })}
      </div>
    </SchemableFormVariablesContextProvider>
  );
};
